import { Modal, Select } from "antd";
import { useAntdAppMessage } from "../hooks/useAntdAppMessage";
import { CloseOutlined } from "@ant-design/icons";
import { useRef, useState, useEffect } from "react";
import modalStyles from "./addNewProjectModal.module.scss";
import { ReactComponent as ImagePlaceholder } from "../images/placeholder-img.svg";
import CustomButton from "../components/Button";
import { useSecureService } from "../store/service";
import { useAuth } from "../hooks/useAuth";
import { UserApi, Configuration } from '../service';
import type { ApiProjectPostScopeEnum } from "../service/apis/ProjectApi";
import { toSeoString } from "../utils/stringHelpers";
import { findShareMemberByEmail, isUserDeleted, DELETED_USER_LABEL } from "../utils/userPresence";
import { DeletedUserChipAvatar } from "../components/UserPresence/DeletedUserAvatarShell";
import deletedUserUi from "../components/UserPresence/deletedUserUi.module.scss";

interface AddNewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (path?: string) => void;
  defaultScope?: ApiProjectPostScopeEnum;
  editMode?: boolean;
  editData?: any;
  variant?: 'internal' | 'external';
}

const userApi = new UserApi(new Configuration());

const AddNewProjectModal = ({ isOpen, onClose, onSuccess, defaultScope, editMode = false, editData, variant = 'internal' }: AddNewProjectModalProps) => {
  const message = useAntdAppMessage();
  const [thumbnail, setThumbnail] = useState<any>(null);
  const [title, setTitle] = useState("");
  const [organization, setOrganization] = useState("");
  const [share, setShare] = useState<string[]>([]);
  const [shareOptions, setShareOptions] = useState<{ label: string; value: string }[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadRef = useRef<HTMLInputElement>(null);
  const { createProject, editProject } = useSecureService();
  const { canSearchUserEmails } = useAuth();

  useEffect(() => {
    if (editMode && editData) {
      setTitle(editData.name || '');
      setOrganization(editData.organization ?? editData.location ?? '');
      if (editData.share && Array.isArray(editData.share)) {
        const emails = editData.share.map((item: any) => item.email || item).filter((email: any) => typeof email === 'string' && email.trim());
        setShare(emails);
      } else {
        setShare([]);
      }
    }
  }, [editMode, editData, isOpen]);

  const handleClose = () => {
    setTitle("");
    setOrganization("");
    setShare([]);
    setThumbnail(null);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setTitle("");
      setOrganization("");
      setShare([]);
      setThumbnail(null);
    }
  }, [isOpen]);

  const handleImageClick = () => {
    uploadRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setThumbnail(e.target.files[0]);
  };

  const handleShareSearch = async (value: string) => {
    if (!value) {
      setShareOptions([]);
      return;
    }
    if (!canSearchUserEmails) {
      setShareOptions([]);
      return;
    }
    setShareLoading(true);
    try {
      const response = await userApi.apiUserEmailsGet({ searchTerm: value }, { credentials: 'include' });
      if (response.success && response.data?.emails) {
        setShareOptions(
          response.data.emails
            .filter((email: string) => email && typeof email === 'string' && email.trim())
            .map((email: string) => ({ label: email, value: email }))
        );
      } else {
        setShareOptions([]);
      }
    } catch {
      setShareOptions([]);
    } finally {
      setShareLoading(false);
    }
  };

  const handleSave = async () => {
    const name = (title || '').trim() || 'Untitled Project';
    setSaving(true);
    try {
      if (editMode && editData) {
        const editDataToSend: any = { id: editData.id, name, share };
        if (thumbnail) editDataToSend.thumbnail = thumbnail;
        const result = await editProject(editDataToSend);
        if (result?.success) {
          handleClose();
          onSuccess?.();
        } else {
          message.error("Project could not be updated.");
        }
      } else {
        const result = await createProject({
          title: name,
          path: toSeoString(name),
          scope: defaultScope,
          organization: (organization || '').trim() || undefined,
          share: undefined,
          thumbnail: undefined
        });
        if (result?.success) {
          handleClose();
          onSuccess?.(result.data?.path);
        } else {
          message.error("Project could not be created.");
        }
      }
    } catch {
      message.error(editMode ? "An error occurred while updating the project." : "An error occurred while creating the project.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      centered
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      title={
        <div className={modalStyles.modalHeader}>
          <span className={modalStyles.modalTitle}>{editMode ? 'Edit Project' : 'New Project'}</span>
          <CloseOutlined onClick={handleClose} className={modalStyles.closeIcon} />
        </div>
      }
      className={`${modalStyles.addNewProjectModal} ${variant === 'external' ? modalStyles.addNewProjectModalExternal : ''}`}
      width={560}
      closable={false}
    >
      <div className={modalStyles.modalContent}>
        <div className={modalStyles.inputsRow}>
          <div className={modalStyles.inputWrap}>
            <input
              type="text"
              className={modalStyles.input}
              placeholder="Untitled Project"
              value={title}
              onChange={e => setTitle(e.target.value)}
              aria-label="Project name"
            />
          </div>
          <div className={modalStyles.inputWrap}>
            <input
              type="text"
              className={modalStyles.input}
              placeholder="Add Organization"
              value={organization}
              onChange={e => setOrganization(e.target.value)}
              aria-label="Organization"
            />
          </div>
        </div>

        {editMode && (
          <div className={modalStyles.editSection}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'rgba(246,255,237,0.6)', marginBottom: 8 }}>Thumbnail</div>
              <div
                onClick={handleImageClick}
                style={{
                  width: 120,
                  height: 90,
                  borderRadius: 8,
                  background: 'rgba(51,77,64,0.5)',
                  border: '1px solid rgba(0,208,125,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  overflow: 'hidden'
                }}
              >
                {thumbnail ? (
                  <img src={URL.createObjectURL(thumbnail)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : editData?.thumbnailUrl ? (
                  <img src={editData.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <ImagePlaceholder style={{ width: 48, height: 48, opacity: 0.6 }} />
                )}
              </div>
              <input ref={uploadRef} id="add-project-cover-input" name="projectCover" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
            </div>
            <div style={{ marginBottom: 0 }}>
              <div style={{ fontSize: 12, color: 'rgba(246,255,237,0.6)', marginBottom: 8 }}>Share With</div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Add email(s)"
                value={share}
                onChange={setShare}
                onSearch={handleShareSearch}
                options={shareOptions}
                filterOption={false}
                notFoundContent={
                  <div style={{ color: '#F6FFED', background: '#222', padding: 8, textAlign: 'center', borderRadius: 8 }}>
                    {shareLoading ? 'Loading...' : 'No email found'}
                  </div>
                }
                showSearch
                tagRender={() => <span style={{ display: 'none' }} />}
              />
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {share.filter(email => typeof email === 'string' && email.trim()).map(email => {
                  const member = findShareMemberByEmail(email, editData?.share);
                  const deleted = isUserDeleted(member);
                  return (
                  <div
                    key={email}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#363B3F',
                      borderRadius: 20,
                      padding: '4px 12px',
                      color: '#F6FFED',
                      fontWeight: 500
                    }}
                  >
                    <DeletedUserChipAvatar deleted={deleted} sizePx={24} style={{ marginRight: 8 }}>
                      <span
                        style={{
                          background: 'var(--app-accent-secondary)',
                          borderRadius: '50%',
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 16
                        }}
                      >
                        {email?.[0]?.toUpperCase() ?? '?'}
                      </span>
                    </DeletedUserChipAvatar>
                    <span className={deleted ? deletedUserUi.nameDeleted : undefined}>
                      {email}
                      {deleted ? (
                        <span className={deletedUserUi.deletedSuffix}>
                          {' '}
                          {DELETED_USER_LABEL}
                        </span>
                      ) : null}
                    </span>
                    <span style={{ marginLeft: 8, cursor: 'pointer' }} onClick={() => setShare(share.filter(e => e !== email))}>
                      ×
                    </span>
                  </div>
                );
                })}
              </div>
            </div>
          </div>
        )}

        <div className={modalStyles.actionButtons}>
          <CustomButton onClick={handleClose} className={modalStyles.cancelButton}>
            Cancel
          </CustomButton>
          <CustomButton onClick={handleSave} loading={saving} className={modalStyles.primaryButton}>
            {editMode ? 'Update' : 'Create New Project'}
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default AddNewProjectModal;
